import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Send, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";

const MAX_MS = 5 * 60 * 1000;

function fmt(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function VoiceRecorder({ onSend, disabled, onRecordingChange, onCancel }) {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const cleanup = () => {
    clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  };

  useEffect(() => () => cleanup(), []);

  const start = async () => {
    if (disabled) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Voice recording isn't supported on this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg"].find(
        (t) => window.MediaRecorder.isTypeSupported(t)
      );
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = (mimeType || "audio/webm").split(";")[0];
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setState("preview");
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };
      startedAtRef.current = Date.now();
      setElapsed(0);
      setState("recording");
      onRecordingChange?.(true);
      rec.start();
      timerRef.current = setInterval(() => {
        const e = Date.now() - startedAtRef.current;
        setElapsed(e);
        if (e >= MAX_MS) stop();
      }, 200);
    } catch (e) {
      logError("VoiceRecorder.start", e);
      if (e && e.name === "NotAllowedError") {
        toast.error("Microphone permission denied. Enable it in your browser settings.");
      } else {
        toast.error("Could not start recording.");
      }
      cleanup();
      setState("idle");
    }
  };

  const stop = () => {
    onRecordingChange?.(false);
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch (e) {
        /* ignore */
      }
    }
  };

  const cancel = () => {
    onRecordingChange?.(false);
    onCancel?.();
    cleanup();
    setBlob(null);
    setElapsed(0);
    setState("idle");
  };

  const send = async () => {
    if (!blob) return;
    setState("uploading");
    try {
      await onSend(blob, elapsed);
      cancel();
    } catch (e) {
      setState("preview");
    }
  };

  if (state === "idle") {
    return (
      <button
        type="button"
        onClick={start}
        disabled={disabled}
        title="Record voice message"
        data-testid="btn-record-voice"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-pink-100 hover:text-pink-500 disabled:opacity-40"
      >
        <Mic className="h-4 w-4" />
      </button>
    );
  }

  if (state === "recording") {
    return (
      <div className="flex flex-1 items-center gap-2 rounded-full bg-red-50 px-3" data-testid="voice-recording">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inset-0 animate-ping rounded-full bg-red-400" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
        </span>
        <span className="flex-1 font-mono text-sm text-red-600" data-testid="voice-elapsed">{fmt(elapsed)}</span>
        <button
          type="button"
          onClick={cancel}
          title="Cancel"
          data-testid="btn-cancel-voice"
          className="rounded-full p-1.5 text-slate-500 hover:bg-white"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={stop}
          title="Stop"
          data-testid="btn-stop-voice"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
        >
          <Square className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const url = blob ? URL.createObjectURL(blob) : null;
  return (
    <div className="flex flex-1 items-center gap-2 rounded-full bg-slate-50 px-3" data-testid="voice-preview">
      {url && <audio src={url} controls preload="metadata" className="h-9 flex-1" data-testid="voice-preview-audio" />}
      <button
        type="button"
        onClick={cancel}
        disabled={state === "uploading"}
        title="Discard"
        data-testid="btn-discard-voice"
        className="rounded-full p-1.5 text-slate-500 hover:bg-white"
      >
        <X className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={send}
        disabled={state === "uploading"}
        title="Send voice message"
        data-testid="btn-send-voice"
        className="flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-white hover:bg-pink-600 disabled:opacity-40"
      >
        {state === "uploading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </button>
    </div>
  );
}
